import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import qs from 'querystring';
import { GeneralModal } from 'Components/modal/new/GeneralModal';
import ConfigVersionMessage from 'Components/utility/ConfigVersionMessage';
import Providers from 'Components/utility/Providers';
import UnexpectedError from 'Components/utility/UnexpectedError';
import { setUrls } from 'Utilities/dataHelpers';
import { getConfig } from 'Utilities/envConfig';
import * as log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import { configureStore } from 'Utilities/store';
import 'Utilities/testHelper';
import App from './App';
import './services';
if (process.env.NODE_ENV !== 'production') {
  import('./dev');
}
let gotEnvConfig = false;
let store;
let _errorReset = () => {
  //
};
const container = document.getElementById('app');
const root = createRoot(container);

// Renders our UnexpectedError display and minimally bootstraps i18n
const renderError = err => {
  mediator.publish('init:i18n');
  window.setTimeout(() => {
    root.render(/*#__PURE__*/React.createElement("div", {
      className: "d-flex flex-column w-100 bg-white p-5",
      style: {
        height: '100vh'
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "d-flex justify-content-between flex-wrap"
    }, process.env.NODE_ENV !== 'production' && /*#__PURE__*/React.createElement("span", null, err.toString()), gotEnvConfig && /*#__PURE__*/React.createElement(ConfigVersionMessage, null)), /*#__PURE__*/React.createElement(UnexpectedError, {
      reset: _errorReset
    })));
  }, 0);
};

// Finally render our React trees
const renderApp = () => {
  try {
    root.render(/*#__PURE__*/React.createElement(Provider, {
      store: store
    }, /*#__PURE__*/React.createElement(Providers, null, /*#__PURE__*/React.createElement(App, null), /*#__PURE__*/React.createElement(GeneralModal, null))));
  } catch (e) {
    renderError(e);
    log.error(e);
  }
};

// Let our services know that the app is now ready to start
const startApp = () => {
  mediator.publish('store:created');
};

// Attempt to load a token from the url query on first try, otherwise start the app
const noCurrentSession = () => {
  log.debug('no current session');
  startApp();
};

// Attempt to get the user data based on session storage and fetch their config before starting the app
// fallback to cookies if storage is empty/bad
const trySession = () => {
  // Server failed to get legacy token from FSLL
  if (window.location.pathname === '/tokenerror') {
    const querystring = (window.location.search.split('?')[1] || '').split('#')[0];
    const query = qs.decode(querystring);
    return renderError(new Error(`failed to get legacy token from redirect, status: ${query.code}`));
  }
  noCurrentSession();
};
// We load the full config from server and then try to load a user session
const initialize = config => {
  setUrls(config);
  mediator.request('config:getBase').then(res => {
    store = configureStore({
      config: res
    });
    setUrls(res);
    trySession();
  }).catch(err => {
    log.error('failed to get server config: ' + err);
    renderError(new Error('failed to get server config: ' + err));
  });
};

// This is the starting point of our app launch life-cycle
const init = () => {
  getConfig().then(config => {
    gotEnvConfig = true;
    log.init(config.logLevel);
    if (config.mock) {
      mediator.once('MOCK:setup:intialized', () => initialize(config));
      import('./services/mock');
    } else {
      initialize(config);
    }
  }).catch(err => {
    log.error('failed to get the environment config: ' + err);
    renderError(new Error('failed to get the environment config: ' + err));
  });
};

// Begin
init();
_errorReset = () => {
  init();
};

// Services that need to publish that they are ready
const appReady = {
  i18n: false,
  nav: false,
  router: false,
  state: false
};

// Wait for the api to finish setting a ticket from query then try to start a session again
const tokenSub = mediator.once('app:queryTokenRead', () => trySession());

// Listens for all services to be complete so that we have everything we need when we mount React
const readySub = mediator.subscribe('app:ready', ready => {
  Object.assign(appReady, ready);
  const done = Object.values(appReady).every(value => !!value);

  // i18n sets the document title, so we tell our router to 'refresh' to get the
  // document title to update on Android
  if (ready.i18n) {
    mediator.publish('init:router');
  }
  if (done) {
    mediator.publish('app:mounted');
    mediator.remove('app:queryTokenRead', tokenSub.id);
    mediator.remove('app:ready', readySub.id);
  }
});
mediator.once('app:mounted', renderApp);